import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    resetOnPropsChange?: boolean;
    resetKeys?: Array<string | number>;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    errorId: string;
}

/**
 * Error Boundary component specifically designed for API integration failures
 * Provides graceful fallback UI and error reporting for production capability API calls
 */
export class APIErrorBoundary extends Component<Props, State> {
    private resetTimeoutId: number | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: '',
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
            errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error for debugging
        console.error('APIErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo,
        });

        // Call the onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Auto-reset after 30 seconds to allow for recovery
        this.resetTimeoutId = window.setTimeout(() => {
            this.handleReset();
        }, 30000);
    }

    componentDidUpdate(prevProps: Props) {
        const { resetOnPropsChange, resetKeys } = this.props;
        const { hasError } = this.state;

        // Reset error boundary when resetKeys change
        if (hasError && resetOnPropsChange && resetKeys) {
            const prevResetKeys = prevProps.resetKeys || [];
            const hasResetKeyChanged = resetKeys.some(
                (key, index) => key !== prevResetKeys[index]
            );

            if (hasResetKeyChanged) {
                this.handleReset();
            }
        }
    }

    componentWillUnmount() {
        if (this.resetTimeoutId) {
            clearTimeout(this.resetTimeoutId);
        }
    }

    handleReset = () => {
        if (this.resetTimeoutId) {
            clearTimeout(this.resetTimeoutId);
            this.resetTimeoutId = null;
        }

        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: '',
        });
    };

    handleRetry = () => {
        this.handleReset();
    };

    render() {
        const { hasError, error, errorId } = this.state;
        const { children, fallback } = this.props;

        if (hasError) {
            // Custom fallback UI provided
            if (fallback) {
                return fallback;
            }

            // Default error UI
            return (
                <div className="api-error-boundary">
                    <div className="error-container">
                        <div className="error-header">
                            <h3>Something went wrong</h3>
                            <span className="error-id">Error ID: {errorId}</span>
                        </div>
                        
                        <div className="error-message">
                            <p>
                                We encountered an issue while loading production capabilities. 
                                This might be a temporary problem with the API service.
                            </p>
                            
                            {error && (
                                <details className="error-details">
                                    <summary>Technical Details</summary>
                                    <pre className="error-stack">
                                        {error.message}
                                        {error.stack && (
                                            <>
                                                {'\n\n'}
                                                {error.stack}
                                            </>
                                        )}
                                    </pre>
                                </details>
                            )}
                        </div>

                        <div className="error-actions">
                            <button 
                                className="retry-button primary"
                                onClick={this.handleRetry}
                            >
                                Try Again
                            </button>
                            
                            <button 
                                className="reload-button secondary"
                                onClick={() => window.location.reload()}
                            >
                                Reload Page
                            </button>
                        </div>

                        <div className="error-help">
                            <p>
                                If this problem persists, please contact the development team 
                                and include the Error ID above.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return children;
    }
}

/**
 * Hook-based wrapper for functional components
 */
export const withAPIErrorBoundary = <P extends object>(
    Component: React.ComponentType<P>,
    errorBoundaryProps?: Omit<Props, 'children'>
) => {
    const WrappedComponent = (props: P) => (
        <APIErrorBoundary {...errorBoundaryProps}>
            <Component {...props} />
        </APIErrorBoundary>
    );

    WrappedComponent.displayName = `withAPIErrorBoundary(${Component.displayName || Component.name})`;
    
    return WrappedComponent;
};

/**
 * Utility function to create error boundary props for specific use cases
 */
export const createAPIErrorBoundaryProps = (
    context: 'gsla' | 'r2f' | 'general' = 'general'
): Omit<Props, 'children'> => {
    const contextMessages = {
        gsla: 'GSLA production capabilities',
        r2f: 'R2F production capabilities', 
        general: 'production capabilities'
    };

    return {
        onError: (error, errorInfo) => {
            // Log to console with context
            console.error(`API Error in ${context} context:`, {
                error: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                context,
                timestamp: new Date().toISOString(),
            });

            // In a real application, you might want to send this to an error reporting service
            // Example: errorReportingService.captureException(error, { context, errorInfo });
        },
        resetOnPropsChange: true,
    };
};